import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CountryRegionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCityMutations } from '~/hooks/use-cities';
import { citySchema, EMPTY_CITY, type CityFormValues } from '~/lib/schemas/city';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: CityFormValues | null;
	regions: CountryRegionToken[];
}

/** Add / edit a city. RHF + zod, with a region (`crid`) dropdown. */
export function CityEditDialog({ open, onOpenChange, initial, regions }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCityMutations();
	const form = useForm<CityFormValues>({
		resolver: zodResolver(citySchema) as Resolver<CityFormValues>,
		defaultValues: initial ?? EMPTY_CITY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_CITY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const crid = watch('crid');
	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.cities.editTitle') : t('system.cities.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.cities.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.cities.columns.region')} error={formState.errors.crid?.message}>
						<Select
							value={crid ? String(crid) : ''}
							onValueChange={v => setValue('crid', Number(v), { shouldDirty: true, shouldValidate: true })}>
							<SelectTrigger className="w-full" aria-label={t('system.cities.columns.region')}>
								<SelectValue placeholder={t('system.cities.regionPlaceholder')} />
							</SelectTrigger>
							<SelectContent>
								{regions.map(r => (
									<SelectItem key={r.id} value={String(r.id)}>
										{r.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<FormField label={t('system.cities.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.cities.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
						</FormField>
						<FormField label={t('system.cities.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<FormField label={t('system.fields.erpCode')}>
						<Input {...register('erpcd')} />
					</FormField>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="city-visible">{t('system.cities.columns.visible')}</Label>
						<Switch id="city-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
